import React, { useEffect } from 'react'
import { styled } from '@mui/material/styles'
import {
  Editor,
  EditorState,
  RichUtils,
  getDefaultKeyBinding,
  convertFromHTML,
} from 'draft-js'
import { Grid } from '@mui/material'
import InlineStyleControls from './InlineStyleControls'
import BlockStyleControls from './BlockStyleControls'
import { stateToHTML } from 'draft-js-export-html'
import Draft from 'draft-js/lib/Draft'
import PropTypes from 'prop-types'
import ThemeConfig from '../../theme/ThemeConfig'
import clsx from 'clsx'
import { UploadButton } from './UploadButton'

const PREFIX = 'RichTextEditor'

const classes = {
  root: `${PREFIX}-root`,
  editor: `${PREFIX}-editor`,
  controls: `${PREFIX}-controls`,
  hidePlaceholder: `${PREFIX}-hidePlaceholder`,
  hasFocus: `${PREFIX}-hasFocus`,
  placeHolderStyle: `${PREFIX}-placeHolderStyle`,
}

const Root = styled('div')(() => {
  return {
    [`&.${classes.root}`]: {
      backgroundColor: '#fff',
      fontFamily: ThemeConfig.typography.fontFamily,
      fontSize: '14px',
      position: 'relative',
      marginLeft: '2px',
    },
    [`& .${classes.editor}`]: {
      border: '2px solid #9ecaed',
      borderRadius: '7px',
      boxShadow: '0 0 10px #9ecaed',
      cursor: 'text',
      padding: '10px 15px',
      lineHeight: '1.5em',
      fontSize: '16px',
      fontFamily: ThemeConfig.typography.fontFamily,
      color: '#000',
      minHeight: '40px',
      maxHeight: props => props?.editorMaxHeight ?? '150px',
      overflow: 'auto',
    },
    [`& .${classes.controls}`]: {
      fontFamily: ThemeConfig.typography.fontFamily,
      fontSize: '12px',
      marginBottom: '10px',
      userSelect: 'none',
    },
    [`& .${classes.hidePlaceholder}`]: {
      display: 'none',
    },
    [`& .${classes.hasFocus}`]: {
      color: '#bdc1c9',
    },
    [`& .${classes.placeHolderStyle}`]: {
      color: '#000',
    },
  }
})

const createInitialState = content => {
  if (content) {
    const blocksFromHTML = convertFromHTML(content)
    if (blocksFromHTML.contentBlocks !== null) {
      const initialState = Draft.ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      )
      return EditorState.createWithContent(initialState)
    }
  }

  return EditorState.createEmpty()
}

const RichTextEditor = props => {
  const { blockTypes, inlineStyles, onClick, placeHolderText, onUpdate } = props
  const editor = React.useRef()
  const [editorState, setEditorState] = React.useState(
    createInitialState(props.content),
  )
  const [clearContentSwitch, setClearContentSwitch] = React.useState(
    props.clearContentSwitch,
  )

  const handleDraftJsOnChange = changedState => {
    if (typeof changedState !== 'undefined') {
      setEditorState(changedState)
    }
  }

  const htmlContent = stateToHTML(editorState.getCurrentContent())
  useEffect(() => {
    // invoke onUpdate with the current html content of the editor
    onUpdate && onUpdate(htmlContent)
  }, [htmlContent, onUpdate])

  const handleOnClick = event => {
    editor.current.focus()
    onClick && onClick(event)
  }

  const toggleInlineStyle = style => {
    handleDraftJsOnChange(RichUtils.toggleInlineStyle(editorState, style))
  }

  const toggleBlockStyle = block => {
    handleDraftJsOnChange(RichUtils.toggleBlockType(editorState, block))
  }

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      handleDraftJsOnChange(newState)
      return true
    }
    return false
  }

  const onTabCommand = e => {
    if (e.keyCode === 9 /* TAB */) {
      const newEditorState = RichUtils.onTab(e, editorState, 4 /* maxDepth */)
      if (newEditorState !== editorState) {
        handleDraftJsOnChange(newEditorState)
      }
      return
    }
    return getDefaultKeyBinding(e)
  }

  useEffect(() => {
    editor.current.focus()

    if (props.clearContentSwitch !== clearContentSwitch) {
      setClearContentSwitch(props.clearContentSwitch)
      setEditorState(createInitialState())
    }
  }, [props.clearContentSwitch, clearContentSwitch])

  const hidePlaceholder =
    editorState.getCurrentContent().hasText() &&
    editorState.getCurrentContent().getBlockMap().first().getType() !==
      'unstyled'

  return (
    <Root className={classes.root}>
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <BlockStyleControls
          editorState={editorState}
          classes={classes}
          blockTypes={blockTypes}
          onToggle={toggleBlockStyle}
        />
        <InlineStyleControls
          editorState={editorState}
          classes={classes}
          inlineStyles={inlineStyles}
          onToggle={toggleInlineStyle}
        />
        {props.onUpload && <UploadButton onUpload={props.onUpload} />}
      </Grid>
      <div
        className={clsx(
          classes.editor,
          {
            'RichEditor-hidePlaceholder': hidePlaceholder,
          },
          { 'public-DraftEditorPlaceholder-root': !hidePlaceholder },
          { [classes.placeHolderStyle]: !hidePlaceholder },
        )}
        onClick={handleOnClick}
      >
        <Editor
          editorState={editorState}
          spellCheck={true}
          ref={editor}
          onChange={handleDraftJsOnChange}
          handleKeyCommand={handleKeyCommand}
          onTab={onTabCommand}
          placeholder={placeHolderText}
        />
      </div>
    </Root>
  )
}

RichTextEditor.propTypes = {
  blockTypes: PropTypes.array,
  inlineStyles: PropTypes.array,
  onUpdate: PropTypes.func,
  onClick: PropTypes.func,
  placeHolderText: PropTypes.string,
  content: PropTypes.string,
  clearContentSwitch: PropTypes.bool,
  editorMaxHeight: PropTypes.string,
  onUpload: PropTypes.func,
}

export default RichTextEditor
